import { layoutTokens, typographyTokens, surfaceTokens } from "@/shared/theme";
import { Box, ButtonBase, Typography } from "@mui/material";
import type { PatternStat } from "../patterns.api";
import { formatMotif } from "../format";
import { StatusBadge } from "@/shared/components/StatusBadge";
import { InfoTitle } from "./PatternTypography";
import { PatternMovementRail } from "./EvidenceRail";

type PatternSignalRowProps = {
  pattern: PatternStat;
  first: boolean;
  selected: boolean;
  analyzedGames: number;
  evidenceSummary: string | null;
  motifDescription: string;
  onSelect: (pattern: PatternStat) => void;
};

export const PatternSignalRow = ({
  pattern,
  first,
  selected,
  analyzedGames,
  evidenceSummary,
  motifDescription,
  onSelect,
}: PatternSignalRowProps) => {
  const volume = pattern.volume;
  const affectedGames = volume?.affectedGames ?? pattern.gameCount;
  const evidenceInfo = volume
    ? `${motifDescription} Evidence: ${volume.affectedGames} of ${volume.analyzedGames} same-format games, ${volume.occurrences} of ${volume.eligibleDecisions} eligible decisions (${(volume.recurrenceRate * 100).toFixed(volume.recurrenceRate < 0.1 ? 1 : 0)}%; expected range ${Math.round(volume.interval95[0] * 100)}–${Math.round(volume.interval95[1] * 100)}%).`
    : `${motifDescription} Evidence: ${pattern.count} verified moments across ${pattern.gameCount} of ${analyzedGames} same-format games. Reanalyze to add the calibrated decision range.`;
  const row = (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "3rem minmax(0, 1fr) auto" },
        gap: 1,
        alignItems: "center",
        px: { xs: 2, sm: 2.5 },
        py: 1.25,
        borderTop: first ? "none" : layoutTokens.dividerBorder,
        borderColor: "divider",
        bgcolor: selected ? surfaceTokens.hover : "transparent",
      }}
    >
      <Typography
        sx={{
          display: { xs: "none", sm: "block" },
          color: "primary.main",
          fontSize: "h5.fontSize",
          fontWeight: "fontWeightBold",
          lineHeight: typographyTokens.lineHeight.heading,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {pattern.count}
      </Typography>
      <Box>
        <InfoTitle
          info={evidenceInfo}
          titleSx={{
            fontFamily: "fontFamily",
            fontSize: { xs: "h6.fontSize", sm: "h6.fontSize" },
            fontWeight: "fontWeightBold",
          }}
        >
          {formatMotif(pattern.motif)}
        </InfoTitle>
        {evidenceSummary && (
          <Typography
            sx={{
              mt: 0.25,
              color: "text.secondary",
              fontFamily: "fontFamily",
              fontSize: "body2.fontSize",
              lineHeight: typographyTokens.lineHeight.body,
            }}
          >
            {evidenceSummary}
          </Typography>
        )}
        <PatternMovementRail movement={pattern.movement} />
      </Box>
      <Box sx={{ textAlign: "right" }}>
        <StatusBadge
          label={selected ? "Selected" : "Verified"}
          tone={selected ? "active" : "neutral"}
          sx={{ mb: 0.5, justifyContent: "flex-end" }}
        />
        <Typography
          sx={{
            color: "text.primary",
            fontFamily: "fontFamily",
            fontSize: "body2.fontSize",
            fontWeight: "fontWeightBold",
            whiteSpace: "nowrap",
          }}
        >
          {affectedGames} of {volume?.analyzedGames ?? analyzedGames} games
        </Typography>
        <Typography
          sx={{
            mt: 0.25,
            color: pattern.examples.length > 0 ? "primary.main" : "text.secondary",
            fontSize: "caption.fontSize",
            whiteSpace: "nowrap",
            fontWeight: pattern.examples.length > 0 ? "fontWeightBold" : "fontWeightRegular",
          }}
        >
          {pattern.examples.length > 0 ? "PULL A MOVE →" : (volume?.maturity ?? "developing").toUpperCase()}
        </Typography>
      </Box>
    </Box>
  );

  return pattern.examples.length > 0 ? (
    <ButtonBase
      onClick={() => onSelect(pattern)}
      aria-label={`Show ${formatMotif(pattern.motif)} examples`}
      aria-pressed={selected}
      sx={{
        display: "block",
        width: "100%",
        textAlign: "left",
        "&:hover > div": { bgcolor: surfaceTokens.selected },
        "&:focus-visible": {
          outline: `${layoutTokens.structuralBorder}px solid`,
          outlineColor: "primary.main",
          outlineOffset: -2,
        },
      }}
    >
      {row}
    </ButtonBase>
  ) : (
    row
  );
};
